import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { ASSESSMENT_OPTIONS, REGIONS } from "@/data/site";
import { Eyebrow, FadeIn, MaskedLines, usePageMeta } from "@/components/blocks";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-ink/25 bg-transparent px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 transition-colors focus:border-ink focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60";

const BUDGETS = [
  "₹9,999 / $299 per month",
  "₹18,999 / $499 per month",
  "Not sure yet",
];

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  country: "",
  age_range: "",
  primary_goal: "",
  experience: "",
  injuries: "",
  coaching_preference: "",
  budget: "",
  referral: "",
  notes: "",
};

function ChipGroup({ label, options, value, onSelect, name }) {
  return (
    <fieldset>
      <legend className={labelCls}>{label}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            data-testid={`${name}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onSelect(o)}
            aria-pressed={value === o}
            className={`border px-4 py-2.5 text-xs transition-colors duration-300 sm:text-sm ${
              value === o
                ? "border-lime bg-lime text-white"
                : "border-ink/25 text-ink/75 hover:border-ink hover:text-ink"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export default function Start() {
  usePageMeta(
    "Start Your Assessment | StrengthLeap",
    "Tell us where you are and where you want to go. A StrengthLeap coach reviews every assessment personally."
  );

  const [form, setForm] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const guided = p.get("track") === "guided";
    return {
      ...INITIAL,
      coaching_preference: guided ? "Guided Coaching — Async" : "",
      notes: guided && p.get("concession") === "student" ? "Student concession request — valid student ID available." : "",
    };
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [leadId, setLeadId] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please add your name and email so we can respond.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setLeadId(data.id || "");
      setStatus("done");
      window.scrollTo({ top: 0 });
    } catch {
      setStatus("idle");
      setError("Something went wrong submitting your assessment. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <main className="bg-bone text-ink">
        <section data-testid="assessment-confirmation" className="relative mx-auto flex min-h-[100svh] max-w-[1440px] flex-col justify-center px-6 py-32 sm:px-10 lg:px-16">
          <div className="absolute inset-x-0 top-0 h-20 bg-ink" aria-hidden="true" />
          <FadeIn>
            <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/60">
              <span className="flex h-6 w-6 items-center justify-center bg-lime">
                <Check className="h-4 w-4 text-white" />
              </span>
              Assessment received
            </p>
          </FadeIn>
          <h1 className="mt-8 font-display text-[12vw] font-black uppercase leading-[0.92] tracking-tight sm:text-[7vw] lg:text-[5.5vw]">
            <MaskedLines lines={["Your leap", "has begun."]} delay={0.15} />
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke sm:text-lg">
              A StrengthLeap coach reviews every assessment personally. Expect a reply at the email you provided with
              a recommended program and next steps.
            </p>
            {leadId && (
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">
                Reference — {leadId.slice(0, 8).toUpperCase()}
              </p>
            )}
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/programs"
                data-testid="confirmation-cta-programs"
                className="group inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-coal"
              >
                Explore Programs
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                to="/"
                data-testid="confirmation-cta-home"
                className="inline-flex items-center gap-3 border border-ink/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:border-ink"
              >
                Back to Home
              </Link>
            </div>
          </FadeIn>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-bone text-ink">
      <section className="bg-ink px-6 pb-16 pt-36 text-bone sm:px-10 sm:pt-44 lg:px-16">
        <div className="mx-auto max-w-[1440px]">
          <FadeIn>
            <Eyebrow>Book Your Assessment</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-display text-[12vw] font-black uppercase leading-[0.92] tracking-tight sm:text-[8vw] lg:text-[6vw]">
            <MaskedLines lines={["Tell us where", "you want to go."]} delay={0.1} />
          </h1>
          <FadeIn delay={0.45}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ash sm:text-lg">
              This assessment takes a few minutes. It tells us where you are, what’s in the way, and which
              StrengthLeap program fits.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="px-6 py-20 sm:px-10 sm:py-24 lg:px-16">
        <form
          data-testid="assessment-form"
          onSubmit={submit}
          className="mx-auto grid max-w-[1440px] gap-14 lg:grid-cols-[1fr_1.6fr] lg:gap-24"
        >
          <div className="space-y-10">
            <FadeIn>
              <div>
                <label htmlFor="af-name" className={labelCls}>
                  Name *
                </label>
                <input id="af-name" data-testid="assessment-input-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
              </div>
            </FadeIn>
            <FadeIn delay={0.05}>
              <div>
                <label htmlFor="af-email" className={labelCls}>
                  Email *
                </label>
                <input id="af-email" data-testid="assessment-input-email" type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div>
                <label htmlFor="af-phone" className={labelCls}>
                  Phone / WhatsApp
                </label>
                <input id="af-phone" data-testid="assessment-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 …" />
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div>
                <label htmlFor="af-country" className={labelCls}>
                  Country
                </label>
                <select id="af-country" data-testid="assessment-input-country" className={inputCls} value={form.country} onChange={set("country")}>
                  <option value="">Select country</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <ChipGroup label="Age range" name="age" options={ASSESSMENT_OPTIONS.ageRanges} value={form.age_range} onSelect={set("age_range")} />
            </FadeIn>
            <FadeIn delay={0.25}>
              <ChipGroup label="How did you hear about us?" name="referral" options={ASSESSMENT_OPTIONS.referral} value={form.referral} onSelect={set("referral")} />
            </FadeIn>
          </div>

          <div className="space-y-10">
            <FadeIn>
              <ChipGroup label="Primary goal" name="goal" options={ASSESSMENT_OPTIONS.goals} value={form.primary_goal} onSelect={set("primary_goal")} />
            </FadeIn>
            <FadeIn delay={0.05}>
              <ChipGroup label="Training experience" name="experience" options={ASSESSMENT_OPTIONS.experience} value={form.experience} onSelect={set("experience")} />
            </FadeIn>
            <FadeIn delay={0.1}>
              <div>
                <label htmlFor="af-injuries" className={labelCls}>
                  Current injuries or pain
                </label>
                <textarea id="af-injuries" data-testid="assessment-input-injuries" rows={3} className={inputCls} value={form.injuries} onChange={set("injuries")} placeholder="e.g. recurring lower-back pain, old knee injury — or write 'none'" />
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <ChipGroup label="Preferred coaching" name="coaching" options={ASSESSMENT_OPTIONS.coaching} value={form.coaching_preference} onSelect={set("coaching_preference")} />
            </FadeIn>
            <FadeIn delay={0.2}>
              <ChipGroup label="Budget range" name="budget" options={BUDGETS} value={form.budget} onSelect={set("budget")} />
            </FadeIn>
            <FadeIn delay={0.25}>
              <div>
                <label htmlFor="af-notes" className={labelCls}>
                  Anything else we should know?
                </label>
                <textarea id="af-notes" data-testid="assessment-input-notes" rows={3} className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Schedule constraints, event dates, training history…" />
              </div>
            </FadeIn>

            {error && (
              <p data-testid="assessment-form-error" className="border border-red-900/40 bg-red-950/10 px-4 py-3 text-sm text-red-800">
                {error}
              </p>
            )}

            <FadeIn delay={0.3}>
              <button
                type="submit"
                data-testid="assessment-form-submit-button"
                disabled={status === "sending"}
                className="group inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-5 font-mono text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-coal disabled:opacity-60 sm:w-auto"
              >
                {status === "sending" ? "Submitting…" : "Submit Assessment"}
                <ArrowUpRight className="h-4 w-4 text-lime transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <p className="mt-5 max-w-md font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-ink/45">
                Your details are used only to prepare your coaching recommendation. No spam, no lists.
              </p>
            </FadeIn>
          </div>
        </form>
      </section>
    </main>
  );
}
